import type { XpaSupportedComponentsResolverProps } from "@/components/xpa-layout/XpaLayout";
import { XpaLayout } from "@/components/xpa-layout/XpaLayout";
import { HorizontalSeparator } from "@/xpa-components/horizontal-separator/HorizontalSeparator";
import { PublisherImprintLink } from "@/xpa-components/publisher-imprint-link/PublisherImprintLink";
import { RelatedNews } from "@/xpa-components/related-news/RelatedNews";
import { RelatedVideos } from "@/xpa-components/related-videos/RelatedVideos";
import { Title } from "@/xpa-components/title/Title";
import { VisibilityTracker } from "@/xpa-components/visibility-tracker/VisibilityTracker";
import { fetchXpaLayoutWithResponseInfo } from "@/utils/fetchXpaLayout";
import type { Layout } from "@motain/xpa-proto-files-web/lib/runtime/xpa";
import type {
  GetServerSideProps,
  InferGetServerSidePropsType,
  NextPage,
} from "next";
import { useEffect } from "react";
import { ArticleHeader } from "@/xpa-components/article-header/ArticleHeader";
import styles from "@/styles/pages/video/Video.module.scss";
import { YoutubeVideo } from "@/xpa-components/youtube-video/YoutubeVideo";
import { NativeAppsPromotionBannerComponent } from "@/xpa-components/native-apps-promotion-banner/NativeAppsPromotionBanner";
import { MatchGallery } from "@/xpa-components/match-gallery/MatchGallery";
import { XpaGoogleAdsPlaceholder } from "@/modules/ads";
import { UpcomingMatches } from "@/xpa-components/upcoming-matches/UpcomingMatches";
import { Gallery } from "@/xpa-components/gallery/Gallery";
import { DefaultVideojsWrapper } from "@/xpa-components/videojs-wrapper/DefaultVideojsWrapper";
import { MaxRootWidthWrapper } from "@/components/max-root-width-wrapper/MaxRootWidthWrapper";
import { EntityPromotionBanner } from "@/xpa-components/entity-promotion-banner/EntityPromotionBanner";
import { VideoErrorBanner } from "@/xpa-components/video-error-banner/VideoErrorBanner";
import {
  clientSideParamsService,
  StreamName,
  useRegisterStream,
  useRegisterXpaStream,
} from "@/modules/analytics";
import { VisibilityAlert } from "@/components/visibility-alert/VisibilityAlert";
import { ClientParameterType } from "@motain/xpa-proto-files-web/lib/types/tracking";

const Video: NextPage<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = (props) => {
  useRegisterStream(StreamName.ClipsDetail);
  useRegisterXpaStream(props);

  useEffect(() => {
    clientSideParamsService.clear();
  }, []);

  return (
    <div className={styles.xpaContainer}>
      <XpaLayout
        layout={props}
        XpaSupportedComponentsResolver={XpaSupportedComponentsResolver}
      />
      <VisibilityAlert
        onVisible={() => {
          clientSideParamsService.set(
            ClientParameterType.PARAMETER_SCROLL_TO_BOTTOM,
            true,
          );
        }}
      />
    </div>
  );
};

export const getServerSideProps: GetServerSideProps<Layout> = async (ctx) => {
  const layoutResponse = await fetchXpaLayoutWithResponseInfo(ctx);

  if (layoutResponse.status === 404) {
    return { notFound: true };
  } else {
    return { props: layoutResponse.layout };
  }
};

export default Video;

const XpaSupportedComponentsResolver: XpaSupportedComponentsResolverProps = ({
  contentType,
  uiKey,
}) => {
  switch (contentType.$case) {
    case "title":
      return <Title {...contentType.title} />;
    case "articleHeader":
      return <ArticleHeader {...contentType.articleHeader} />;
    case "googleAdsPlaceholder":
      return <XpaGoogleAdsPlaceholder {...contentType.googleAdsPlaceholder} />;
    case "videojsPlayer":
      return (
        <DefaultVideojsWrapper
          videoJsOptions={contentType.videojsPlayer}
          id={uiKey}
        />
      );
    case "publisherImprintLink":
      return (
        <MaxRootWidthWrapper>
          <PublisherImprintLink {...contentType.publisherImprintLink} />
        </MaxRootWidthWrapper>
      );
    case "horizontalSeparator":
      return <HorizontalSeparator {...contentType.horizontalSeparator} />;
    case "relatedNews":
      return <RelatedNews {...contentType.relatedNews} />;
    case "relatedVideos":
      return <RelatedVideos {...contentType.relatedVideos} />;
    case "visibilityTracker":
      return (
        <>
          <VisibilityTracker {...contentType.visibilityTracker} />
          <VisibilityAlert
            onVisible={() => {
              clientSideParamsService.set(
                ClientParameterType.PARAMETER_SCROLL_TO_END_OF_SECTION,
                true,
              );
            }}
          />
        </>
      );

    case "youtube":
      return <YoutubeVideo {...contentType.youtube} />;
    case "upcomingMatches":
      return <UpcomingMatches {...contentType.upcomingMatches} />;
    case "gallery":
      return <Gallery {...contentType.gallery} />;
    case "nativeAppsPromotionBanner":
      return (
        <div className={"enforced-light"}>
          <NativeAppsPromotionBannerComponent
            {...contentType.nativeAppsPromotionBanner}
          />
        </div>
      );
    case "entityPromotionBanner":
      return <EntityPromotionBanner {...contentType.entityPromotionBanner} />;
    case "videoErrorBanner":
      return <VideoErrorBanner {...contentType.videoErrorBanner} />;
    case "matchGallery":
      return <MatchGallery {...contentType.matchGallery} />;
    default:
      return null;
  }
};
